/**
 * @module contexts/LanguageProvider
 * @author Charles Blais <charles.blais@canada.ca>
 */

import React, { createContext, useContext, useReducer } from "react";

export const initialStateEnglish = {
  lang: 'en',
  stationTitle: 'Station information for',
  error: 'Error!',
  errorDetails: 'There was an error when loading the network configuration.  Refresh your web browser and if the problem persists, please let us know.',
  loading: 'Loading...',
  loadingDetails: 'We are loading the network information, please wait.',
  network: 'Network',
  station: 'Station',
  location: 'Location',
  channel: 'Channel',
  summaryTitle: 'Summary information',
  summaryEmpty: 'Please select a station on the map to show summary information.',
  completeList: 'Complete list of stations',
  channelsHistory: 'Channels Information',
  name: 'Name',
  status: 'Status',
  elevation: 'Elevation',
  startDate: 'Start Date',
  endDate: 'End Date',
  open: 'Open',
  close: 'Closed',
  planned: 'Planned',
  meters: 'meters',
  details: 'Info',
  moreDetails: 'More details',
  search: 'Search',
  records: 'records',
  all: 'All',
  mapGrayscale: 'Grayscale',
  mapStreet: 'Street',
  sampleRate: <><abbr title="Sample rate">SR</abbr> (<abbr title="hertz">Hz</abbr>)</>,
  samplePeriod: <><abbr title="Sample period">SP</abbr> (<abbr title="second">s</abbr>)</>,
  azimuth: <abbr title="Azimuth">A</abbr>,
  dip: <abbr title="Dip">D</abbr>,
  reset: 'Reset',
  sensor: 'Sensor',
  networkCodes: {
    CN: 'Canadian National Seismic Network (CN)',
    C2: 'Canadian National Geomagnetic Network (C2)',
    QW: 'Canadian National Earthquake Early Warning Network (QW)',
  },
  instructionsLabel: 'Instructions',
  instructions: 'The map shows stations (circle) associated to networks operated by the Canadian Hazard Information Service (CHIS). The color inside the circle are open (green) and closed (red) stations.  These can be toggled from the bottom right menu.  You can select a specific network from the top right menu on the map.  You can click on any circles for additional information.',
  showClosed: 'Show',
  hideClosed: 'Hide',
  basemap: {
    url: "https://www.earthquakescanada.nrcan.gc.ca/server2_serveur2/rest/services/BaseMaps/CBMT3978/MapServer/tile/{z}/{y}/{x}",
    attribution: "&#169; His Majesty the King in Right of Canada, as represented by the Minister of Natural Resources",
  },
};


export const initialStateFrench = {
  lang: 'fr',
  stationTitle: 'Informations sur la station',
  error: 'Erreur!',
  errorDetails: 'Une erreur s\'est produite lors du chargement de la configuration réseau. Rafraîchir votre navigateur Web et si le problème persiste, veuillez nous en informer.',
  loading: 'Chargement...',
  loadingDetails: 'Nous chargeons les informations réseau, veuillez patienter.',
  network: 'Réseau',
  station: 'Station',
  location: 'Location',
  channel: 'Cannal',
  summaryTitle: 'Informations récapitulatives',
  summaryEmpty: 'Veuillez sélectionner une station sur la carte pour obtenir des informations récapitulatives.',
  completeList: 'Liste complète des stations',
  channelsHistory: 'Information des canaux',
  name: 'Nom',
  status: 'Statut',
  elevation: 'Élévation',
  startDate: 'Date de début',
  endDate: 'Date de fin',
  open: 'Ouverte',
  close: 'Fermée',
  planned: 'Planifiée',
  meters: 'mètres',
  details: 'Info',
  moreDetails: 'Plus de détails',
  search: 'Chercher',
  records: 'records',
  all: 'Tous',
  mapGrayscale: 'Niveaux de gris',
  mapStreet: 'Rue',
  sampleRate: <><abbr title="Taux d'échantillonnage">TE</abbr> (<abbr title="hertz">Hz</abbr>)</>,
  samplePeriod: <><abbr title="Période d'échantillonnage">PE</abbr> (<abbr title="seconde">s</abbr>)</>,
  reset: 'Réinitialiser',
  sensor: 'Capteur',
  azimuth: <abbr title="Azimuth">A</abbr>,
  dip: <abbr title="Dip">D</abbr>,
  networkCodes: {
    CN: 'Réseau sismique national canadien (CN)',
    C2: 'Réseau géomagnétique national canadien (C2)',
    QW: 'Réseau national canadien d\'alerte sismique précoce (QW)'
  },
  instructionsLabel: 'Instructions',
  instructions: 'La carte montre les stations (cercle) associées aux réseaux exploités par le Service Canadien d\'Information sur les Risques (SCIR). La couleur à l\'intérieur du cercle correspond aux stations ouvertes (vertes) et fermées (rouges).  Celles-ci peuvent être basculées à partir du menu en bas à droite.  Vous pouvez sélectionner un réseau spécifique à partir du menu supérieur droit de la carte.  Vous pouvez cliquer sur n\'importe quel cercle pour obtenir des informations supplémentaires.',
  showClosed: 'Afficher',
  hideClosed: 'Cacher',
  basemap: {
    url: "https://www.earthquakescanada.nrcan.gc.ca/server2_serveur2/rest/services/BaseMaps/CBCT3978/MapServer/tile/{z}/{y}/{x}",
    attribution: "&#169; Sa Majesté le Rois du Chef du Canada, représentée par le ministre des Ressources naturelles",
  },
};


const reducer = (state, action) => {
  switch (action.type) {
    default:
      return state;
  }
}


export const LanguageContext = createContext();


export const LanguageProvider = ({ initialState, children }) => (
  <LanguageContext.Provider value={useReducer(reducer, initialState)}>
    {children}
  </LanguageContext.Provider>
);


export const useLanguageState = () => useContext(LanguageContext);
